/**
 * Project-aware three-dimensional viewer.
 *
 * All geometry and engineering overlays come from canonical project/backend
 * contracts. Rendering policy decides only whether a roof is shown as a fully
 * detailed engineering roof or as a neutral contextual mass.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AmbientLight, Box3, BufferGeometry, CanvasTexture, DirectionalLight, DoubleSide,
  ExtrudeGeometry, Float32BufferAttribute, Group, LineBasicMaterial, LineSegments,
  Mesh, MeshBasicMaterial, MeshStandardMaterial, Path, PerspectiveCamera, Scene,
  Shape, ShapeGeometry, Sprite, SpriteMaterial, TOUCH, Vector3, WebGLRenderer,
} from 'three';
import type { Object3D } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import type {
  CalculationResponse, Project, RoofModule, WindZoneGeometry, ZoneResult,
} from '../../gen/ejot/roofing/v1/types';
import { polygonRings } from '../../shared/polygonRings';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import { fromContract, type GeometryModel } from '../roof-plan-editor/model';
import type { LayerVisibility } from '../roof-plan-editor/render';
import { readViewerPalette, type ViewerPalette } from '../viewer-3d/palette';
import { ResourceRegistry } from '../viewer-3d/resources';
import { displayZoneFact, zoneEngineeringLabel } from '../wind-zone-plan/zonePresentation';
import './ProjectViewer3D.css';

const MM_TO_M = 0.001;

export type ProjectViewerDetailMode = 'all' | 'active';
export type ProjectRoofRenderMode = 'detailed' | 'context';

export interface ProjectViewerRoof {
  id: string;
  label: string;
  module: RoofModule;
  model: GeometryModel;
  roofHeightMillimetres: number;
  zones: readonly WindZoneGeometry[];
  results: readonly ZoneResult[];
  renderMode: ProjectRoofRenderMode;
}

export function assembleProjectViewerRoofs(
  project: Project,
  result: CalculationResponse | undefined,
  activeModuleId: string | undefined,
  detailMode: ProjectViewerDetailMode,
): ProjectViewerRoof[] {
  const resultByModule = new Map(
    (result?.modules ?? []).map((moduleResult) => [moduleResult.module_id ?? '', moduleResult]),
  );
  const modules = project.modules ?? [];
  const fallbackActiveId = activeModuleId || modules[0]?.id;

  return modules.flatMap((module, index) => {
    const plan = module.build_up?.plan;
    if (!plan) return [];
    const model = fromContract(plan);
    if (!model.closed || model.points.length < 3) return [];

    const id = module.id ?? `roof-${index + 1}`;
    const detailed = detailMode === 'all' || id === fallbackActiveId;
    const moduleResult = resultByModule.get(module.id ?? '');
    return [{
      id,
      label: module.display_name?.trim() || `Roof ${index + 1}`,
      module,
      model,
      roofHeightMillimetres: module.roof_height_above_ground?.millimetres ?? 0,
      zones: detailed ? (moduleResult?.geometry?.wind_zones ?? []) : [],
      results: detailed ? (moduleResult?.zones ?? []) : [],
      renderMode: detailed ? 'detailed' : 'context',
    }];
  });
}

export function ProjectViewer3D({
  project,
  result,
  activeModuleId,
  detailMode,
  layers,
  theme,
  selectedZoneId,
  onZoneSelect,
  onActiveModuleChange,
  onOpenModule,
}: {
  project: Project;
  result: CalculationResponse | undefined;
  activeModuleId?: string;
  detailMode: ProjectViewerDetailMode;
  layers: LayerVisibility;
  theme: string;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
  onActiveModuleChange?: (moduleId: string) => void;
  onOpenModule?: (moduleId: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<ProjectViewerScene>();
  const [ready, setReady] = useState(false);
  const roofs = useMemo(
    () => assembleProjectViewerRoofs(project, result, activeModuleId, detailMode),
    [activeModuleId, detailMode, project, result],
  );
  const selectedRoof = roofs.find((roof) => roof.id === activeModuleId) ?? roofs[0];
  const selectedZone = selectedRoof?.zones.find((zone) => zone.id === selectedZoneId);
  const selectedResult = selectedRoof?.results.find((zone) => zone.zone_id === selectedZoneId);
  const selectedFact = displayZoneFact(selectedResult);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const scene = new ProjectViewerScene(host);
    sceneRef.current = scene;
    setReady(true);
    const observer = new ResizeObserver(() => scene.resize());
    observer.observe(host);
    return () => {
      observer.disconnect();
      scene.dispose();
      sceneRef.current = undefined;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    sceneRef.current?.refreshTheme();
    sceneRef.current?.build(roofs);
  }, [ready, roofs, theme]);

  useEffect(() => {
    if (ready) sceneRef.current?.fit();
  }, [ready, roofs]);

  useEffect(() => {
    if (ready) sceneRef.current?.setLayers(layers);
  }, [ready, layers]);

  useEffect(() => {
    if (ready) sceneRef.current?.setSelectedZone(selectedZoneId);
  }, [ready, selectedZoneId]);

  if (roofs.length === 0) {
    return (
      <div className="viewer-overlay" role="status">
        <p>No roof geometry is available for the selected project state.</p>
      </div>
    );
  }

  return (
    <div className="viewer-3d project-viewer-3d">
      <div className="project-viewer-toolbar" role="group" aria-label="Project roof selection">
        <div className="project-viewer-roof-buttons">
          {roofs.map((roof) => (
            <button
              key={roof.id}
              type="button"
              className={roof.id === selectedRoof?.id ? 'toolbar-button is-active' : 'toolbar-button'}
              aria-pressed={roof.id === selectedRoof?.id}
              onClick={() => onActiveModuleChange?.(roof.id)}
            >
              {roof.label}
            </button>
          ))}
        </div>
        {selectedRoof && onOpenModule && (
          <button type="button" className="toolbar-button" onClick={() => onOpenModule(selectedRoof.id)}>
            Open {selectedRoof.label}
          </button>
        )}
      </div>

      {onZoneSelect && selectedRoof && selectedRoof.zones.length > 0 && (
        <div className="viewer-toolbar" role="group" aria-label="Select 3D wind zone">
          {selectedRoof.zones.map((zone) => (
            <button
              key={zone.id}
              type="button"
              className={zone.id === selectedZoneId ? 'toolbar-button is-active' : 'toolbar-button'}
              aria-pressed={zone.id === selectedZoneId}
              onClick={() => onZoneSelect(zone.id === selectedZoneId ? undefined : zone.id)}
            >
              Zone {zone.display_label || zone.id || '—'}
            </button>
          ))}
        </div>
      )}

      <div className="viewer-stage">
        <div className="viewer-host" ref={hostRef} aria-label="Interactive multi-roof 3D verification view" />
        <CoordinateIndicator mode="3d" visible={layers.axes !== false} />
        <div className="viewer-navigation-controls" role="group" aria-label="3D navigation controls">
          <button type="button" className="viewer-nav-button" aria-label="Zoom in" onClick={() => sceneRef.current?.zoomBy(0.82)}>+</button>
          <button type="button" className="viewer-nav-button" aria-label="Zoom out" onClick={() => sceneRef.current?.zoomBy(1.22)}>-</button>
          <button type="button" className="viewer-nav-button is-primary" aria-label="Zoom extents" onClick={() => sceneRef.current?.fit()}>Extents</button>
          <span className="viewer-touch-hint">1 finger: orbit · 2 fingers: zoom + pan</span>
        </div>
      </div>

      {detailMode === 'active' && roofs.length > 1 && (
        <div className="viewer-note project-viewer-context-note">
          The selected roof carries engineering colours and labels. Other roofs are neutral grey context masses only.
        </div>
      )}

      {selectedZone && (
        <div className="viewer-note" role="status" aria-live="polite">
          Selected zone: <strong>{selectedZone.display_label || selectedZone.id}</strong>
          {selectedFact.kind !== 'unavailable' && <> · {selectedFact.valueText}</>}.
        </div>
      )}
    </div>
  );
}

class ProjectViewerScene {
  readonly scene = new Scene();
  private readonly renderer: WebGLRenderer;
  private readonly camera: PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly registry = new ResourceRegistry();
  private readonly content = new Group();
  private readonly layerGroups = new Map<string, Group>();
  private palette: ViewerPalette;
  private readonly container: HTMLElement;
  private animationHandle = 0;
  private disposed = false;
  private bounds = { centre: new Vector3(), radius: 10 };

  constructor(container: HTMLElement) {
    this.container = container;
    this.palette = readViewerPalette(container);
    this.renderer = new WebGLRenderer({ antialias: true, alpha: false });
    const coarsePointer = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    const mobilePixelRatioCap = coarsePointer || window.innerWidth <= 900 ? 1.5 : 2;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobilePixelRatioCap));
    this.renderer.setSize(container.clientWidth || 1, container.clientHeight || 1, false);
    container.appendChild(this.renderer.domElement);
    this.scene.background = this.palette.background;
    this.camera = new PerspectiveCamera(45, this.aspect(), 0.1, 5000);
    this.camera.position.set(40, 30, 40);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.rotateSpeed = 1.8;
    this.controls.zoomSpeed = 1.4;
    this.controls.panSpeed = 1.5;
    this.controls.screenSpacePanning = true;
    this.controls.zoomToCursor = true;
    this.controls.enableRotate = true;
    this.controls.enablePan = true;
    this.controls.enableZoom = true;
    this.controls.touches.ONE = TOUCH.ROTATE;
    this.controls.touches.TWO = TOUCH.DOLLY_PAN;

    const key = new DirectionalLight(0xffffff, 2.0);
    key.position.set(40, 60, 30);
    const fill = new DirectionalLight(0xffffff, 0.6);
    fill.position.set(-30, 20, -40);
    this.scene.add(key, fill, new AmbientLight(0xffffff, 1.2));
    this.scene.add(this.content);
    this.start();
  }

  resize(): void {
    if (this.disposed) return;
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  refreshTheme(): void {
    this.palette = readViewerPalette(this.container);
    this.scene.background = this.palette.background;
  }

  build(roofs: readonly ProjectViewerRoof[]): void {
    if (this.disposed) return;
    this.clearContent();
    for (const roof of roofs) this.buildRoof(roof);
    this.updateBounds();
  }

  private buildRoof(roof: ProjectViewerRoof): void {
    const shape = this.buildShape(roof.model);
    const roofHeight = Math.max(roof.roofHeightMillimetres * MM_TO_M, 0.5);
    const context = roof.renderMode === 'context';

    this.addLayer('building-mass', this.buildBuildingMass(shape, roofHeight, context));
    this.addLayer('roof-surface', this.buildRoofSurface(shape, roofHeight, context));
    this.addLayer('roof-outline', this.buildRoofOutline(roof.model, roofHeight, context));

    if (!context) {
      this.addLayer('wind-zones', this.buildZones(roof.zones, roofHeight));
      this.addLayer('zone-labels', this.buildZoneLabels(roof, roofHeight));
    }
  }

  private buildShape(model: GeometryModel): Shape {
    const shape = new Shape();
    model.points.forEach((point, index) => {
      const x = point.x * MM_TO_M;
      const y = point.y * MM_TO_M;
      if (index === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
    });
    shape.closePath();
    for (const opening of model.openings) {
      if (opening.points.length < 3) continue;
      const hole = new Path();
      opening.points.forEach((point, index) => {
        const x = point.x * MM_TO_M;
        const y = point.y * MM_TO_M;
        if (index === 0) hole.moveTo(x, y); else hole.lineTo(x, y);
      });
      hole.closePath();
      shape.holes.push(hole);
    }
    return shape;
  }

  private buildBuildingMass(shape: Shape, roofHeight: number, context: boolean): Object3D {
    const geometry = this.registry.track(new ExtrudeGeometry(shape, { depth: roofHeight, bevelEnabled: false }));
    geometry.rotateX(-Math.PI / 2);
    const material = this.registry.track(new MeshStandardMaterial({
      color: this.palette.buildingMass,
      roughness: 0.9,
      metalness: 0,
      transparent: true,
      opacity: context ? 0.78 : 0.52,
    }));
    return new Mesh(geometry, material);
  }

  private buildRoofSurface(shape: Shape, roofHeight: number, context: boolean): Object3D {
    const geometry = this.registry.track(new ShapeGeometry(shape));
    geometry.rotateX(-Math.PI / 2);
    geometry.translate(0, roofHeight + 0.01, 0);
    const material = this.registry.track(new MeshStandardMaterial({
      color: context ? this.palette.buildingMass : this.palette.roofSurface,
      roughness: 0.95,
      metalness: 0,
      side: DoubleSide,
      transparent: context,
      opacity: context ? 0.95 : 1,
    }));
    return new Mesh(geometry, material);
  }

  private buildRoofOutline(model: GeometryModel, roofHeight: number, context: boolean): Object3D {
    const group = new Group();
    const material = this.registry.track(new LineBasicMaterial({
      color: context ? this.palette.buildingMass : this.palette.outline,
      transparent: context,
      opacity: context ? 0.88 : 1,
    }));
    const addRing = (points: readonly { x: number; y: number }[]) => {
      const positions: number[] = [];
      for (let index = 0; index < points.length; index += 1) {
        const a = points[index]!;
        const b = points[(index + 1) % points.length]!;
        positions.push(a.x * MM_TO_M, roofHeight + 0.02, -a.y * MM_TO_M);
        positions.push(b.x * MM_TO_M, roofHeight + 0.02, -b.y * MM_TO_M);
      }
      const geometry = this.registry.track(new BufferGeometry());
      geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
      group.add(new LineSegments(geometry, material));
    };
    addRing(model.points);
    if (!context) {
      for (const opening of model.openings) if (opening.points.length >= 3) addRing(opening.points);
    }
    return group;
  }

  private buildZones(zones: readonly WindZoneGeometry[], roofHeight: number): Object3D {
    const group = new Group();
    for (const zone of zones) {
      const rings = polygonRings(zone.outline);
      if (rings.length === 0 || rings[0]!.length < 3) continue;
      const shape = new Shape();
      rings[0]!.forEach((point, index) => {
        const x = (point.x ?? 0) * MM_TO_M;
        const y = (point.y ?? 0) * MM_TO_M;
        if (index === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
      });
      shape.closePath();
      for (const ring of rings.slice(1)) {
        if (ring.length < 3) continue;
        const hole = new Path();
        ring.forEach((point, index) => {
          const x = (point.x ?? 0) * MM_TO_M;
          const y = (point.y ?? 0) * MM_TO_M;
          if (index === 0) hole.moveTo(x, y); else hole.lineTo(x, y);
        });
        hole.closePath();
        shape.holes.push(hole);
      }
      const geometry = this.registry.track(new ShapeGeometry(shape));
      geometry.rotateX(-Math.PI / 2);
      geometry.translate(0, roofHeight + 0.03, 0);
      const material = this.registry.track(new MeshBasicMaterial({
        color: this.palette.zone(zone.zone_class),
        side: DoubleSide,
      }));
      const mesh = new Mesh(geometry, material);
      mesh.name = zone.id ?? '';
      group.add(mesh);
    }
    return group;
  }

  private buildZoneLabels(roof: ProjectViewerRoof, roofHeight: number): Object3D {
    const group = new Group();
    const resultByZone = new Map(roof.results.map((result) => [result.zone_id ?? '', result]));
    const coarsePointer = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    const textureScale = coarsePointer || window.innerWidth <= 900 ? 0.5 : 1;

    for (const zone of roof.zones) {
      if (!zone.id || !zone.label_anchor) continue;
      const label = zoneEngineeringLabel(zone, resultByZone.get(zone.id));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(1920 * textureScale);
      canvas.height = Math.round((label.pressureText ? 660 : 450) * textureScale);
      const context = canvas.getContext('2d');
      if (!context) continue;
      const foreground = `#${this.palette.outline.getHexString()}`;
      const halo = `#${this.palette.roofSurface.getHexString()}`;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.lineJoin = 'round';
      context.fillStyle = foreground;
      context.strokeStyle = halo;
      const drawText = (text: string, y: number, font: string, strokeWidth: number) => {
        context.font = font;
        context.lineWidth = strokeWidth;
        context.strokeText(text, canvas.width / 2, y * textureScale);
        context.fillText(text, canvas.width / 2, y * textureScale);
      };
      drawText(
        label.zoneText,
        label.pressureText ? 234 : (canvas.height / 2) / textureScale,
        `800 ${234 * textureScale}px "Roboto Condensed", Arial, sans-serif`,
        18 * textureScale,
      );
      if (label.pressureText) {
        drawText(
          label.pressureText,
          486,
          `700 ${138 * textureScale}px "Roboto Condensed", Arial, sans-serif`,
          12 * textureScale,
        );
      }
      const texture = this.registry.track(new CanvasTexture(canvas));
      texture.needsUpdate = true;
      const material = this.registry.track(new SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }));
      const sprite = new Sprite(material);
      sprite.name = `zone-label-${zone.id}`;
      sprite.position.set(
        (zone.label_anchor.x ?? 0) * MM_TO_M,
        roofHeight + 0.35,
        -(zone.label_anchor.y ?? 0) * MM_TO_M,
      );
      const aspect = canvas.width / canvas.height;
      const height = label.pressureText ? 5.4 : 4.2;
      const baseX = height * aspect;
      sprite.scale.set(baseX, height, 1);
      sprite.userData.baseScaleX = baseX;
      sprite.userData.baseScaleY = height;
      sprite.renderOrder = 100;
      group.add(sprite);
    }
    return group;
  }

  private addLayer(id: string, object: Object3D): void {
    let group = this.layerGroups.get(id);
    if (!group) {
      group = new Group();
      group.name = id;
      this.layerGroups.set(id, group);
      this.content.add(group);
    }
    group.add(object);
  }

  setLayers(visibility: LayerVisibility): void {
    for (const [id, group] of this.layerGroups) group.visible = visibility[id] !== false;
  }

  setSelectedZone(zoneId: string | undefined): void {
    const labelGroup = this.layerGroups.get('zone-labels');
    labelGroup?.traverse((node) => {
      if (!(node instanceof Sprite) || !(node.material instanceof SpriteMaterial)) return;
      const selected = Boolean(zoneId && node.name === `zone-label-${zoneId}`);
      const baseX = Number(node.userData.baseScaleX) || node.scale.x;
      const baseY = Number(node.userData.baseScaleY) || node.scale.y;
      const scale = selected ? 1.12 : 1;
      node.scale.set(baseX * scale, baseY * scale, 1);
      node.material.opacity = 1;
    });
  }

  fit(): void {
    const distance = this.fitDistance();
    const offset = new Vector3(0.7, 0.6, 0.7).normalize().multiplyScalar(distance);
    this.camera.position.copy(this.bounds.centre.clone().add(offset));
    this.controls.target.copy(this.bounds.centre);
    this.controls.update();
  }

  zoomBy(factor: number): void {
    if (!Number.isFinite(factor) || factor <= 0) return;
    const offset = this.camera.position.clone().sub(this.controls.target);
    const currentDistance = offset.length();
    if (currentDistance <= Number.EPSILON) return;
    const minimum = Math.max(this.controls.minDistance, 0.01);
    const maximum = Number.isFinite(this.controls.maxDistance) && this.controls.maxDistance > minimum
      ? this.controls.maxDistance
      : Math.max(this.bounds.radius * 20, minimum * 12);
    const nextDistance = Math.min(maximum, Math.max(minimum, currentDistance * factor));
    offset.setLength(nextDistance);
    this.camera.position.copy(this.controls.target.clone().add(offset));
    this.controls.update();
  }

  private updateBounds(): void {
    const box = new Box3().setFromObject(this.content);
    if (box.isEmpty()) {
      this.bounds = { centre: new Vector3(), radius: 10 };
      this.updateCameraLimits();
      return;
    }
    const centre = new Vector3();
    const size = new Vector3();
    box.getCenter(centre);
    box.getSize(size);
    this.bounds = { centre, radius: Math.max(size.length() / 2, 1) };
    this.updateCameraLimits();
  }

  private updateCameraLimits(): void {
    const radius = Math.max(this.bounds.radius, 1);
    this.controls.minDistance = Math.max(radius * 0.08, 0.25);
    this.controls.maxDistance = Math.max(radius * 20, this.controls.minDistance * 12);
  }

  private fitDistance(): number {
    const radius = Math.max(this.bounds.radius, 1);
    const verticalFov = this.camera.fov * Math.PI / 180;
    const aspect = Math.max(this.camera.aspect, 0.05);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
    const limitingFov = Math.max(Math.min(verticalFov, horizontalFov), Math.PI / 18);
    return (radius / Math.sin(limitingFov / 2)) * 1.12;
  }

  private aspect(): number {
    return (this.container.clientWidth || 1) / (this.container.clientHeight || 1);
  }

  private start(): void {
    const loop = () => {
      if (this.disposed) return;
      this.animationHandle = requestAnimationFrame(loop);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  private clearContent(): void {
    this.registry.disposeAll();
    this.layerGroups.clear();
    this.content.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.animationHandle);
    this.clearContent();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.renderer.domElement.remove();
  }
}
